/**
 * AudioFrame parse + WebCodecs AudioDecoder (Opus native) + WebAudio playback.
 *
 * Wire format (9-byte header):
 *   0     version u8 (=1)
 *   1     flags u8 (bit0=silence)
 *   2-4   pts_90khz u24 BE
 *   5     sample_rate_tag u8 (0=48k, 1=24k, 2=16k, 3=12k, 4=8k)
 *   6     channels u8 (1 veya 2)
 *   7-8   opus_len u16 BE
 *   9+    Opus payload
 *
 * PacketHeader(11) + AudioFrame(9+opus) gelir; upper layer AudioFrame payload
 * slice'ını bu modüle verir.
 */
const SR_MAP = {
    0: 48000, 1: 24000, 2: 16000, 3: 12000, 4: 8000,
};
export function parseAudioFrame(data) {
    if (data.length < 9)
        return null;
    const opusLen = (data[7] << 8) | data[8];
    if (data.length < 9 + opusLen)
        return null;
    return {
        version: data[0],
        silence: (data[1] & 0x1) !== 0,
        pts90Khz: (data[2] << 16) | (data[3] << 8) | data[4],
        sampleRate: SR_MAP[data[5]] ?? 48000,
        channels: data[6],
        opusPayload: data.subarray(9, 9 + opusLen),
    };
}
export class AudioPlayer {
    ctx = null;
    decoder = null;
    schedulingSec = 0;
    lastSampleRate = 48000;
    lastChannels = 2;
    muted = false;
    // Stats
    framesDecoded = 0;
    framesDropped = 0;
    framesPlayed = 0;
    lastError = null;
    async start() {
        if (this.ctx)
            return;
        if (typeof AudioDecoder === 'undefined') {
            this.lastError = 'WebCodecs AudioDecoder API yok (tarayıcı destek?)';
            return;
        }
        this.ctx = new AudioContext({ latencyHint: 'interactive' });
        // Bazı tarayıcılar auto-start yasak; kullanıcı etkileşimi sonrası resume.
        if (this.ctx.state === 'suspended') {
            try {
                await this.ctx.resume();
            }
            catch { /* sessiz */ }
        }
        this.configureDecoder(48000, 2);
    }
    configureDecoder(sampleRate, channels) {
        try {
            this.decoder?.close();
        }
        catch { /* ignore */ }
        this.decoder = new AudioDecoder({
            output: (audioData) => this.onDecoded(audioData),
            error: (e) => { this.lastError = e.message; },
        });
        this.decoder.configure({
            codec: 'opus',
            sampleRate,
            numberOfChannels: channels,
        });
        this.lastSampleRate = sampleRate;
        this.lastChannels = channels;
    }
    setMuted(m) { this.muted = m; }
    isMuted() { return this.muted; }
    push(frame) {
        if (!this.decoder || !this.ctx)
            return;
        if (this.muted) {
            this.framesDropped++;
            return;
        }
        if (frame.silence || frame.opusPayload.length === 0) {
            // DTX (packet loss concealment) — decoder'a null veri göndermiyoruz.
            return;
        }
        if (frame.sampleRate !== this.lastSampleRate ||
            frame.channels !== this.lastChannels) {
            this.configureDecoder(frame.sampleRate, frame.channels);
        }
        try {
            const chunk = new EncodedAudioChunk({
                type: 'key',
                timestamp: frame.pts90Khz * (1_000_000 / 90_000), // µs
                data: frame.opusPayload,
            });
            this.decoder.decode(chunk);
            this.framesDecoded++;
        }
        catch (e) {
            this.lastError = e?.message ?? String(e);
            this.framesDropped++;
        }
    }
    onDecoded(audio) {
        if (!this.ctx) {
            audio.close();
            return;
        }
        const ch = audio.numberOfChannels;
        const nSamples = audio.numberOfFrames;
        const sr = audio.sampleRate;
        // Copy each channel to Float32Array for AudioBuffer
        const buf = this.ctx.createBuffer(ch, nSamples, sr);
        for (let i = 0; i < ch; i++) {
            const chData = new Float32Array(nSamples);
            audio.copyTo(chData, { planeIndex: i, format: 'f32-planar' });
            buf.getChannelData(i).set(chData);
        }
        audio.close();
        const src = this.ctx.createBufferSource();
        src.buffer = buf;
        src.connect(this.ctx.destination);
        const now = this.ctx.currentTime;
        if (this.schedulingSec < now) {
            this.schedulingSec = now + 0.04; // 40ms başlangıç buffer
        }
        src.start(this.schedulingSec);
        this.schedulingSec += buf.duration;
        this.framesPlayed++;
    }
    close() {
        try {
            this.decoder?.close();
        }
        catch { /* */ }
        try {
            void this.ctx?.close();
        }
        catch { /* */ }
        this.decoder = null;
        this.ctx = null;
    }
}
